"use client";

import { useMemo, useState } from "react";
import PriceComponent from "@/src/components/common/PriceComponent";
import { formatAmount } from "@/src/utils/formatters";
import ProductImageSlider from "../sliders/ProductImageSlider";
import Link from "next/link";
import { getMembershipPath, getSingleProductPath } from "@/src/routes/routes";
import { CheckIconSVG, DeliveryAndReturnsSVG } from "@/src/ui/AllSVG";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/src/store/store";
import { closeModal } from "@/src/store/slices/allModalSlice";
import { INewArrivalsAPIResponseDataEntity } from "@/src/types/newArrivals";
import AddToCart from "../../page-components/products/AddToCart";
import { getClientCookieValue } from "@/src/utils/getClientCookieValue";
import AddToWishlist from "../../page-components/products/AddToWishlist";


export default function ProductQuickViewModal() {
    const dispatch = useDispatch();
    const { data: productData } = useSelector((state: RootState) => state.allCommonModal);
    const data = productData as INewArrivalsAPIResponseDataEntity;
    const country = getClientCookieValue("user_country_code");

    const [selectedColor, setSelectedColor] = useState(data?.variants?.[0]?.color ?? data?.colors?.[0]?.name ?? "");
    const [selectedSize, setSelectedSize] = useState(data?.variants?.[0]?.size ?? data?.sizes?.[0]?.name ?? "");

    const selectedVariant = (
        data?.variants?.find(
            v =>
                v.color?.toLowerCase() === selectedColor.toLowerCase() &&
                v.size?.toLowerCase() === selectedSize.toLowerCase()
        ) ??
        data?.variants?.find(
            v => v.color?.toLowerCase() === selectedColor.toLowerCase()
        ) ??
        data?.variants?.[0]
    ) ?? null;

    const isVariant = data?.is_variant || Boolean(selectedVariant);
    // Mirror ProductCard: non-variant products use data.variant as price source
    const priceSource = isVariant ? selectedVariant : data?.variant;

    const stock = isVariant
        ? (selectedVariant?.stock ?? 0)
        : (data?.variant?.stock ?? data?.stock ?? 0);

    const noVariantImages: string[] = [data?.thumbnail ?? '', ...(data?.images ?? [])];
    const activeImages = (
        isVariant
            ? (selectedVariant?.images?.length ? selectedVariant.images : noVariantImages)
            : (noVariantImages ?? [])
    ) ?? [];

    const addToCartPayload = useMemo(() => {
        const sku = isVariant
            ? (selectedVariant?.sku ?? data?.sku ?? "")
            : (data?.variant?.sku ?? data?.sku ?? "");
        return {
            product_id: data?._id ?? "",
            quantity: 1,
            variant_sku: sku,
            country: country ?? "NA",
            currency: data?.pricing_context?.currency ?? "NA",
        };
    }, [data?._id, data?.sku, data?.variant?.sku, data?.pricing_context?.currency, selectedVariant?.sku, isVariant, country]);
    return (
        <>
            <div className="grid grid-cols-1 md:grid-cols-[calc(80vh*4/5)_minmax(0,1fr)] md:h-[80vh] relative">
                <div className="relative bg-secondary-background-color aspect-4/5 md:aspect-auto w-full md:h-full overflow-hidden">
                    <span className="absolute top-3 left-3 text-xs bg-white/40 px-2 py-1 rounded-sm font-medium z-10">
                        {data?.categoryId?.name}
                    </span>
                    <ProductImageSlider images={activeImages as string[]} alt={data?.title ?? ""} objectCover />
                </div>

                {/* Right – product details */}
                <div className="flex flex-col overflow-hidden">
                    <div className="flex flex-col gap-5 p-6! overflow-y-auto custom_scrollbar flex-1">
                        {/* Name */}
                        <h2 className="product_title">
                            {data?.title ?? ""}
                        </h2>

                        {/* Pricing — exclusive items use member pricing only */}
                        {!data?.exclusive && (
                            <div className="space-y-1 text-sm">
                                <p className="text-body-color/55">
                                    <PriceComponent title="Retail Price" price={priceSource?.rental_price ?? data?.rental_price ?? 0} currency={data?.pricing_context?.currency ?? "£"} lineThrough />
                                </p>
                                <p className="text-base font-semibold text-heading-color">
                                    <PriceComponent title="Our Price" price={priceSource?.price ?? data?.price ?? 0} currency={data?.pricing_context?.currency ?? "£"} />
                                </p>
                            </div>
                        )}

                        {/* Members price badge */}
                        <span className="inline-flex w-fit items-center bg-green-color/10 text-green-color text-sm font-medium px-3 py-1 rounded-sm">
                            Members {formatAmount(priceSource?.membership_price ?? data?.membership_price ?? 0, data?.pricing_context?.currency ?? 'EUR')}
                        </span>

                        {/* Colour selector */}
                        {isVariant && <div className="space-y-2.5">
                            <p className="text-xs font-semibold tracking-widest uppercase">
                                Colour:{" "}
                                <span className="font-normal normal-case tracking-normal">
                                    {selectedColor}
                                </span>
                            </p>
                            <div className="flex items-center gap-2 flex-wrap">
                                {data?.colors && data?.colors?.length > 0 && data?.colors?.map((color, index) => (
                                    <button
                                        key={`${color?.name}-${index}`}
                                        title={color?.name}
                                        onClick={() => setSelectedColor(color?.name)}
                                        className={`relative rounded-full min-w-8 min-h-8 border-3 border-white shadow-sm flex items-center justify-center ${selectedColor === color?.name ? 'ring-1 ring-offset-1 ring-muted-color' : ''}`}
                                        style={{ backgroundColor: color?.code }}
                                    >
                                        {selectedColor === color?.name && (
                                            <span className={`[&_svg_path]:fill-white [&_svg_path]:drop-shadow-sm ${color?.name?.toLowerCase() === 'white' ? '[&_svg_path]:fill-black!' : '[&_svg_path]:fill-white'}`}>
                                                <CheckIconSVG />
                                            </span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>}

                        {/* Size selector */}
                        {isVariant && <div className="space-y-2.5">
                            <p className="text-xs font-semibold tracking-widest uppercase">Size:</p>
                            <div className="flex items-center gap-2 flex-wrap">
                                {data?.sizes && data?.sizes?.length > 0 && data?.sizes?.map((size, index) => (
                                    <button
                                        key={index}
                                        onClick={() => setSelectedSize(size?.name)}
                                        className={`uppercase border border-muted-color rounded-md min-w-[48px] px-3.75 py-3.25 text-sm font-medium transition-colors
                                    ${selectedSize === size?.name
                                                ? 'bg-body-color text-white border-body-color    '
                                                : 'bg-white text-body-color border-body-color/20 hover:border-body-color'
                                            }`}
                                    >
                                        {size?.name}
                                    </button>
                                ))}
                            </div>
                            {/* {data?.outOfStockNote && (
                                <p className="text-xs text-body-color/45">{data?.outOfStockNote}</p>
                            )} */}
                        </div>}

                        {/* CTA buttons */}
                        <div className="space-y-2.5">
                            <AddToCart addToCartPayload={addToCartPayload} isAlreadyCart={data?.alreadyCart} isExclusive={data?.exclusive} stock={stock} />
                            <AddToWishlist product_id={data?._id} is_full_btn={true} isAlreadyWishlist={data?.alreadyWishlist} />
                        </div>

                        {/* View full product link */}
                        <Link
                            href={getSingleProductPath(data?.slug ?? "")}
                            className="text-xs text-body-color/80 underline underline-offset-2 text-center hover:text-heading-color transition-colors"
                        >
                            View full product details →
                        </Link>

                        {/* Free shipping */}
                        {/* <div className="flex items-start gap-3 pt-3 border-t border-body-color/10">
                            <div className="shrink-0 mt-0.5">
                                <DeliveryAndReturnsSVG />
                            </div>
                            <div className="text-xs text-body-color/65 space-y-0.5">
                                <p className="font-semibold text-body-color uppercase tracking-wide">Free shipping on all orders</p>
                                <p>Standard Delivery: Receive by Monday, March 02</p>
                                <p>Free returns within 30 days</p>
                            </div>
                        </div> */}

                    </div>

                    <div className="bg-body-color py-3.5 px-5 flex justify-between gap-4 text-xs shrink-0">
                        <p className="text-white">🏅  MEMBERS SAVE £200 ON THIS ITEM</p>
                        <Link
                            href={getMembershipPath()}
                            className="text-[#C8941A]"
                            onClick={() => dispatch(closeModal())}
                        >
                            Join →
                        </Link>
                    </div>
                </div>
            </div>
        </>
    );
}
